import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  HiOutlineCalendarDays,
  HiOutlineCheckCircle,
  HiOutlineLockClosed,
  HiOutlineLockOpen,
  HiOutlinePencilSquare,
  HiOutlinePlus,
  HiOutlineTrash,
} from "react-icons/hi2";

import PageHeader from "../../../components/ui/pageHeader/PageHeader";
import PageCard from "../../../components/ui/pageCard/PageCard";
import PageEmpty from "../../../components/ui/pageEmpty/PageEmpty";
import PageLoader from "../../../components/ui/pageLoader/PageLoader";
import Modal from "../../../components/ui/modal/Modal";
import ConfirmModal from "../../../components/ui/confirmModal/ConfirmModal";
import SchoolYearForm from "../../../components/forms/schoolYear/SchoolYearForm";

import usePermissions from "../../../hooks/usePermissions";
import useToast from "../../../hooks/useToast";

import {
  activateSchoolYear,
  deleteSchoolYear,
  getSchoolYears,
  lockSchoolYear,
  setSelectedSchoolYear,
  unlockSchoolYear,
} from "../../../redux/features/schoolYears/schoolYearSlice";

import "./SchoolYearsPage.css";

const SELECTED_SCHOOL_YEAR_KEY = "power_selected_school_year";

export default function SchoolYearsPage() {
  const dispatch = useDispatch();
  const { can } = usePermissions();
  const toast = useToast();

  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [selectedEditSchoolYear, setSelectedEditSchoolYear] = useState(null);

  const [actionLoading, setActionLoading] = useState({
    id: null,
    type: null,
  });

  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    variant: "danger",
    confirmText: "Bevestigen",
    onConfirm: null,
  });

  const { schoolYears, selectedSchoolYear, isLoading, error } = useSelector(
    (state) => state.schoolYears,
  );

  const sortedSchoolYears = useMemo(() => {
    return [...schoolYears].sort((a, b) => {
      return new Date(b.start_date) - new Date(a.start_date);
    });
  }, [schoolYears]);

  useEffect(() => {
    dispatch(getSchoolYears());
  }, [dispatch]);

  useEffect(() => {
    if (!schoolYears.length) return;

    const storedId = localStorage.getItem(SELECTED_SCHOOL_YEAR_KEY);

    if (!storedId || selectedSchoolYear) return;

    const found = schoolYears.find((item) => item._id === storedId);

    if (found) {
      dispatch(setSelectedSchoolYear(found));
    }
  }, [dispatch, schoolYears, selectedSchoolYear]);

  const closeConfirmModal = () => {
    setConfirmModal({
      isOpen: false,
      title: "",
      message: "",
      variant: "danger",
      confirmText: "Bevestigen",
      onConfirm: null,
    });
  };

  const handleSelect = (schoolYear) => {
    localStorage.setItem(SELECTED_SCHOOL_YEAR_KEY, schoolYear._id);
    dispatch(setSelectedSchoolYear(schoolYear));
  };

  const runAction = async ({
    id,
    type,
    action,
    successTitle,
    successMessage,
  }) => {
    try {
      setActionLoading({ id, type });

      await action();

      toast.success(successTitle, successMessage);
    } catch (error) {
      toast.error("Actie mislukt", error?.message || "Probeer opnieuw.");
    } finally {
      setActionLoading({ id: null, type: null });
    }
  };

  const handleActivate = async (schoolYear) => {
    await runAction({
      id: schoolYear._id,
      type: "activate",
      action: () => dispatch(activateSchoolYear(schoolYear._id)).unwrap(),
      successTitle: "Schooljaar geactiveerd",
      successMessage: `${schoolYear.name} is nu actief.`,
    });
  };

  const handleLock = async (schoolYear) => {
    await runAction({
      id: schoolYear._id,
      type: "lock",
      action: () => dispatch(lockSchoolYear(schoolYear._id)).unwrap(),
      successTitle: "Schooljaar vergrendeld",
      successMessage: `${schoolYear.name} werd vergrendeld.`,
    });
  };

  const handleUnlock = async (schoolYear) => {
    await runAction({
      id: schoolYear._id,
      type: "unlock",
      action: () => dispatch(unlockSchoolYear(schoolYear._id)).unwrap(),
      successTitle: "Schooljaar ontgrendeld",
      successMessage: `${schoolYear.name} is opnieuw bewerkbaar.`,
    });
  };

  const handleDelete = async (schoolYear) => {
    await runAction({
      id: schoolYear._id,
      type: "delete",
      action: () => dispatch(deleteSchoolYear(schoolYear._id)).unwrap(),
      successTitle: "Schooljaar verwijderd",
      successMessage: `${schoolYear.name} werd verwijderd.`,
    });
  };

  const openEdit = (schoolYear) => {
    setSelectedEditSchoolYear(schoolYear);
    setOpenEditModal(true);
  };

  const openActivateConfirm = (schoolYear) => {
    setConfirmModal({
      isOpen: true,
      title: "Schooljaar activeren?",
      message:
        "Het huidige actieve schooljaar wordt gedeactiveerd. Er kan maar één schooljaar actief zijn.",
      variant: "warning",
      confirmText: "Activeer",
      onConfirm: async () => {
        await handleActivate(schoolYear);
        closeConfirmModal();
      },
    });
  };

  const openLockConfirm = (schoolYear) => {
    setConfirmModal({
      isOpen: true,
      title: "Schooljaar vergrendelen?",
      message:
        "Na vergrendeling kan dit schooljaar niet meer aangepast worden.",
      variant: "warning",
      confirmText: "Vergrendel",
      onConfirm: async () => {
        await handleLock(schoolYear);
        closeConfirmModal();
      },
    });
  };

  const openUnlockConfirm = (schoolYear) => {
    setConfirmModal({
      isOpen: true,
      title: "Schooljaar ontgrendelen?",
      message: "Dit schooljaar wordt opnieuw bewerkbaar.",
      variant: "success",
      confirmText: "Ontgrendel",
      onConfirm: async () => {
        await handleUnlock(schoolYear);
        closeConfirmModal();
      },
    });
  };

  const openDeleteConfirm = (schoolYear) => {
    if (schoolYear.is_active) {
      toast.warning(
        "Verwijderen niet mogelijk",
        "Een actief schooljaar kan niet verwijderd worden.",
      );
      return;
    }

    setConfirmModal({
      isOpen: true,
      title: "Schooljaar verwijderen?",
      message:
        "Deze actie kan niet ongedaan gemaakt worden. Verwijderen kan falen als dit schooljaar gekoppelde veldconfiguraties bevat.",
      variant: "danger",
      confirmText: "Verwijderen",
      onConfirm: async () => {
        await handleDelete(schoolYear);
        closeConfirmModal();
      },
    });
  };

  const renderCreateButton = () =>
    can("schoolYear.create") && (
      <button
        className="school-years-page__create-button"
        onClick={() => setOpenCreateModal(true)}
      >
        <HiOutlinePlus />
        <span>Nieuw schooljaar</span>
      </button>
    );

  const renderCreateModal = () => (
    <Modal
      isOpen={openCreateModal}
      onClose={() => setOpenCreateModal(false)}
      title="Nieuw schooljaar"
      description="Maak een nieuw schooljaar aan binnen POWER."
    >
      <SchoolYearForm
        mode="create"
        onSuccess={() => {
          setOpenCreateModal(false);
          toast.success(
            "Schooljaar aangemaakt",
            "Het nieuwe schooljaar werd succesvol aangemaakt.",
          );
        }}
        onCancel={() => setOpenCreateModal(false)}
      />
    </Modal>
  );

  if (isLoading) {
    return <PageLoader title="Schooljaren laden..." />;
  }

  if (!isLoading && schoolYears.length === 0) {
    return (
      <div className="school-years-page">
        <PageHeader
          eyebrow="POWER"
          title="Schooljaren"
          description="Beheer schooljaren en actieve configuraties binnen POWER."
          actions={renderCreateButton()}
        />

        {error && <div className="school-years-page__error">{error}</div>}

        <PageEmpty
          icon={<HiOutlineCalendarDays />}
          title="Geen schooljaren gevonden"
          description="Maak het eerste schooljaar aan om veldconfiguraties en registraties te beheren."
          action={renderCreateButton()}
        />

        {renderCreateModal()}
      </div>
    );
  }

  return (
    <div className="school-years-page">
      <PageHeader
        eyebrow="POWER"
        title="Schooljaren"
        description="Beheer schooljaren en actieve configuraties binnen POWER."
        actions={renderCreateButton()}
      />

      {error && <div className="school-years-page__error">{error}</div>}

      <PageCard className="school-years-page__table-card">
        <div className="school-years-page__table-wrapper">
          <table className="school-years-page__table">
            <thead>
              <tr>
                <th>Schooljaar</th>
                <th>Status</th>
                <th>Actief</th>
                <th>Startdatum</th>
                <th>Einddatum</th>
                <th>Acties</th>
              </tr>
            </thead>

            <tbody>
              {sortedSchoolYears.map((schoolYear) => {
                const isActionLoading = actionLoading.id === schoolYear._id;

                return (
                  <tr
                    key={schoolYear._id}
                    className={
                      selectedSchoolYear?._id === schoolYear._id
                        ? "school-years-page__row--selected"
                        : ""
                    }
                    onClick={() => handleSelect(schoolYear)}
                  >
                    <td>
                      <div className="school-years-page__name">
                        <span>{schoolYear.name}</span>

                        <div className="school-years-page__meta">
                          {schoolYear.is_active && (
                            <span className="school-years-page__active-badge">
                              Actief
                            </span>
                          )}

                          {schoolYear.is_locked && <small>Vergrendeld</small>}
                        </div>
                      </div>
                    </td>

                    <td>
                      {schoolYear.is_locked ? (
                        <span className="school-years-page__badge school-years-page__badge--locked">
                          Locked
                        </span>
                      ) : (
                        <span className="school-years-page__badge school-years-page__badge--open">
                          Open
                        </span>
                      )}
                    </td>

                    <td>
                      {schoolYear.is_active ? (
                        <HiOutlineCheckCircle className="school-years-page__active-icon" />
                      ) : (
                        "-"
                      )}
                    </td>

                    <td>
                      {new Date(schoolYear.start_date).toLocaleDateString(
                        "nl-BE",
                      )}
                    </td>

                    <td>
                      {new Date(schoolYear.end_date).toLocaleDateString(
                        "nl-BE",
                      )}
                    </td>

                    <td>
                      <div className="school-years-page__actions">
                        {can("schoolYear.activate") &&
                          !schoolYear.is_active && (
                            <button
                              className="school-years-page__action--primary"
                              disabled={isActionLoading}
                              onClick={(e) => {
                                e.stopPropagation();
                                openActivateConfirm(schoolYear);
                              }}
                            >
                              {actionLoading.type === "activate" &&
                              isActionLoading
                                ? "..."
                                : "Activeer"}
                            </button>
                          )}

                        {can("schoolYear.lock") && !schoolYear.is_locked && (
                          <button
                            className="school-years-page__action--warning"
                            disabled={isActionLoading}
                            title="Vergrendelen"
                            onClick={(e) => {
                              e.stopPropagation();
                              openLockConfirm(schoolYear);
                            }}
                          >
                            <HiOutlineLockClosed />
                          </button>
                        )}

                        {can("schoolYear.lock") && schoolYear.is_locked && (
                          <button
                            className="school-years-page__action--success"
                            disabled={isActionLoading}
                            title="Ontgrendelen"
                            onClick={(e) => {
                              e.stopPropagation();
                              openUnlockConfirm(schoolYear);
                            }}
                          >
                            <HiOutlineLockOpen />
                          </button>
                        )}

                        {can("schoolYear.update") && (
                          <button
                            disabled={schoolYear.is_locked || isActionLoading}
                            title={
                              schoolYear.is_locked
                                ? "Vergrendelde schooljaren kunnen niet gewijzigd worden"
                                : "Wijzigen"
                            }
                            onClick={(e) => {
                              e.stopPropagation();
                              openEdit(schoolYear);
                            }}
                          >
                            <HiOutlinePencilSquare />
                          </button>
                        )}

                        {can("schoolYear.delete") && (
                          <button
                            className="school-years-page__delete"
                            disabled={schoolYear.is_active || isActionLoading}
                            title={
                              schoolYear.is_active
                                ? "Actieve schooljaren kunnen niet verwijderd worden"
                                : "Verwijderen"
                            }
                            onClick={(e) => {
                              e.stopPropagation();
                              openDeleteConfirm(schoolYear);
                            }}
                          >
                            <HiOutlineTrash />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </PageCard>

      {renderCreateModal()}

      <Modal
        isOpen={openEditModal}
        onClose={() => setOpenEditModal(false)}
        title="Schooljaar wijzigen"
        description="Wijzig de gegevens van dit schooljaar."
      >
        <SchoolYearForm
          mode="edit"
          initialData={selectedEditSchoolYear}
          onSuccess={() => {
            setOpenEditModal(false);
            toast.success(
              "Schooljaar gewijzigd",
              "De wijzigingen werden succesvol opgeslagen.",
            );
          }}
          onCancel={() => setOpenEditModal(false)}
        />
      </Modal>

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        confirmText={confirmModal.confirmText}
        onClose={closeConfirmModal}
        onConfirm={confirmModal.onConfirm}
        isLoading={!!actionLoading.id}
      />
    </div>
  );
}
